import { useCallback, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  CheckCircle2,
  CreditCard,
  Eye,
  FileText,
  Globe,
  Monitor,
  Radio,
  Share2,
  Smartphone,
  Tablet,
  UserPlus,
  XCircle,
  Zap,
} from "lucide-react";
import { api } from "@/lib/api";
import { OpsShell } from "@/components/admin/OpsShell";
import { ago, Bars, Card, dur, Empty, flag, Loading, Pill, T, usePoll } from "@/components/admin/ui";

const KIND = {
  signup: { icon: UserPlus, tone: "text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10" },
  proposal: { icon: FileText, tone: "text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-500/10" },
  share: { icon: Share2, tone: "text-sky-600 dark:text-sky-400 bg-sky-50 dark:bg-sky-500/10" },
  view: { icon: Eye, tone: "text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-500/10" },
  accepted: { icon: CheckCircle2, tone: "text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10" },
  declined: { icon: XCircle, tone: "text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-500/10" },
  payment: { icon: CreditCard, tone: "text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10" },
  failed: { icon: XCircle, tone: "text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-500/10" },
  export: { icon: Zap, tone: "text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/10" },
  team: { icon: UserPlus, tone: "text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800" },
};

const DEVICE = { Desktop: Monitor, Mobile: Smartphone, Tablet: Tablet };

export default function AdminLive() {
  const [live, setLive] = useState(null);
  const [events, setEvents] = useState([]);
  const cursor = useRef(null);

  const load = useCallback(async () => {
    const [{ data: l }, { data: e }] = await Promise.all([
      api.get("/admin/live"),
      api.get(`/admin/live/events${cursor.current ? `?after=${encodeURIComponent(cursor.current)}` : "?limit=40"}`),
    ]);
    setLive(l);
    if (e.items.length) {
      setEvents((prev) => {
        const seen = new Set(prev.map((x) => x.id));
        const fresh = e.items.filter((x) => !seen.has(x.id));
        return fresh.length ? [...fresh, ...prev].slice(0, 60) : prev;
      });
      cursor.current = e.items[0].at;
    } else if (!cursor.current) {
      cursor.current = e.server_time;
    }
  }, []);
  usePoll(load, 8000);

  return (
    <OpsShell
      title="Live view"
      subtitle="Real people on propojo.com right now, and what they are doing"
      actions={<Pill value="live" pulse testid="live-indicator" />}
    >
      {!live ? (
        <Loading label="Connecting to live traffic" />
      ) : (
        <div className="space-y-5" data-testid="admin-live">
          <div className="grid lg:grid-cols-3 gap-4">
            <Card testid="live-now" className="lg:col-span-1">
              <div className="p-5">
                <div className="flex items-center gap-2">
                  <span className="relative flex w-2 h-2">
                    <span className="absolute inline-flex w-full h-full rounded-full bg-emerald-500 opacity-75 animate-ping" />
                    <span className="relative inline-flex w-2 h-2 rounded-full bg-emerald-500" />
                  </span>
                  <p className={T.label}>On the site right now</p>
                </div>
                <p className={`mt-3 text-6xl font-semibold tracking-tight ${T.num}`} data-testid="live-online-count">
                  {live.online}
                </p>
                <p className={`mt-2 text-[13px] ${T.muted}`}>
                  <span className={T.num}>{live.signed_in}</span> signed in ·{" "}
                  <span className={T.num}>{live.anonymous}</span> browsing anonymously
                </p>
                <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between">
                  <span className={`text-[13px] ${T.muted}`}>Unique visitors, 24h</span>
                  <span className={`text-lg font-semibold ${T.num}`} data-testid="live-unique-24h">
                    {live.unique_24h}
                  </span>
                </div>
              </div>
            </Card>

            <Card testid="live-events" title="Live activity" hint="Newest first" className="lg:col-span-2">
              <div className="max-h-[300px] overflow-y-auto">
                {events.length === 0 ? (
                  <Empty label="Waiting for live activity" hint="Signups, proposal opens and payments appear here the moment they happen" icon={Radio} />
                ) : (
                  <ul className="divide-y divide-slate-100 dark:divide-slate-800/70">
                    <AnimatePresence initial={false}>
                      {events.map((e) => {
                        const meta = KIND[e.kind] || KIND.team;
                        const Icon = meta.icon;
                        return (
                          <motion.li
                            key={e.id}
                            layout
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ type: "spring", stiffness: 300, damping: 26 }}
                            className="flex items-center gap-3 px-4 py-2.5"
                            data-testid={`live-event-${e.id}`}
                          >
                            <span className={`w-7 h-7 rounded-lg grid place-items-center shrink-0 ${meta.tone}`}>
                              <Icon className="w-3.5 h-3.5" />
                            </span>
                            <div className="min-w-0">
                              <p className="text-[13px] font-medium text-slate-800 dark:text-slate-200 truncate">{e.title}</p>
                              {e.detail && <p className={`text-xs truncate ${T.muted}`}>{e.detail}</p>}
                            </div>
                            <span className={`ml-auto text-xs shrink-0 ${T.num} ${T.muted}`}>{ago(e.seconds_ago)}</span>
                          </motion.li>
                        );
                      })}
                    </AnimatePresence>
                  </ul>
                )}
              </div>
            </Card>
          </div>

          <Card testid="live-visitors" title="Active visitors" hint={`${live.visitors.length} sessions in the last 45 seconds`}>
            {live.visitors.length === 0 ? (
              <Empty label="Nobody on the site right now" hint="This updates every 8 seconds" icon={Globe} />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse min-w-[860px]">
                  <thead>
                    <tr>
                      <th className={T.th}>Visitor</th>
                      <th className={T.th}>Currently viewing</th>
                      <th className={T.th}>Location</th>
                      <th className={T.th}>Came from</th>
                      <th className={T.th}>Device</th>
                      <th className={T.th}>Pages</th>
                      <th className={T.th}>On site</th>
                      <th className={T.th}>Last action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {live.visitors.map((v) => {
                      const DeviceIcon = DEVICE[v.device] || Monitor;
                      return (
                        <tr key={v.id} className={T.row} data-testid={`live-visitor-${v.id}`}>
                          <td className={T.td}>
                            {v.signed_in ? (
                              <div className="min-w-0">
                                <p className="text-[13px] font-medium text-slate-800 dark:text-slate-200 truncate">{v.email || "Signed in"}</p>
                                <Pill value="active" />
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <span className={`text-xs ${T.num} ${T.muted}`}>{v.id}</span>
                                <Pill value="anonymous" />
                              </div>
                            )}
                          </td>
                          <td className={T.td}>
                            <p className="text-[13px] text-slate-800 dark:text-slate-200">{v.page}</p>
                            <p className={`text-xs ${T.num} ${T.muted} truncate max-w-[220px]`}>{v.path}</p>
                          </td>
                          <td className={T.td}>
                            <span className="text-[13px]">
                              {flag(v.country_code)} {v.city ? `${v.city}, ` : ""}
                              {v.country}
                            </span>
                          </td>
                          <td className={`${T.td} text-[13px]`}>{v.source}</td>
                          <td className={T.td}>
                            <span className="inline-flex items-center gap-1.5 text-[13px]">
                              <DeviceIcon className="w-3.5 h-3.5 text-slate-400" /> {v.device}
                            </span>
                          </td>
                          <td className={`${T.td} ${T.num}`}>{v.views}</td>
                          <td className={`${T.td} ${T.num}`}>{dur(v.session_seconds)}</td>
                          <td className={`${T.td} ${T.num} ${T.muted}`}>{ago(v.idle_seconds)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
            <Card testid="live-by-page" title="Top pages">
              {live.by_page.length ? <Bars rows={live.by_page} /> : <Empty label="No pages yet" />}
            </Card>
            <Card testid="live-by-country" title="Countries">
              {live.by_country.length ? <Bars rows={live.by_country} /> : <Empty label="No locations yet" />}
            </Card>
            <Card testid="live-by-source" title="Traffic sources">
              {live.by_source.length ? <Bars rows={live.by_source} /> : <Empty label="No sources yet" />}
            </Card>
            <Card testid="live-by-device" title="Devices">
              {live.by_device.length ? <Bars rows={live.by_device} /> : <Empty label="No devices yet" />}
            </Card>
          </div>
        </div>
      )}
    </OpsShell>
  );
}
